const mobilePhotoQuery = window.matchMedia('(max-width: 749px)');
let activeMobilePhoto = null;

const limit = (value, min, max) => Math.min(max, Math.max(min, value));
const distance = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
const midpoint = (a, b) => ({ x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 });

function photoUrl(value, width) {
  const url = new URL(value, document.baseURI);
  if (!['https:', 'http:'].includes(url.protocol)) return '';
  if (url.pathname.includes('/cdn/shop/') || url.hostname.endsWith('shopify.com')) {
    url.searchParams.set('width', String(width));
  }
  return url.href;
}

export function openMobilePhoto(images, initialIndex, label, closeLabel) {
  activeMobilePhoto?.close();
  const dialog = document.createElement('dialog');
  dialog.className = 'ochre-mobile-photo';
  dialog.setAttribute('aria-label', label);
  const header = document.createElement('div');
  header.className = 'ochre-mobile-photo__header';
  const counter = document.createElement('span');
  counter.className = 'ochre-mobile-photo__count';
  counter.setAttribute('aria-live', 'polite');
  const closeButton = document.createElement('button');
  closeButton.className = 'ochre-mobile-photo__close';
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', closeLabel);
  closeButton.textContent = '×';
  header.append(counter, closeButton);
  const stage = document.createElement('div');
  stage.className = 'ochre-mobile-photo__stage';
  const image = document.createElement('img');
  image.className = 'ochre-mobile-photo__image';
  image.draggable = false;
  image.decoding = 'async';
  stage.append(image);
  const thumbnails = document.createElement('div');
  thumbnails.className = 'ochre-mobile-photo__thumbs';
  dialog.append(header, stage, thumbnails);
  let index = limit(initialIndex, 0, images.length - 1);
  let scale = 1;
  let x = 0;
  let y = 0;
  let gesture = null;
  let disposed = false;
  const previousOverflow = document.body.style.overflow;

  function draw() {
    const width = stage.clientWidth;
    const height = stage.clientHeight;
    const ratio = image.naturalWidth > 0 && image.naturalHeight > 0 ? image.naturalWidth / image.naturalHeight : 1;
    const fittedWidth = Math.min(width, height * ratio);
    const fittedHeight = fittedWidth / ratio;
    const boundX = Math.max(0, (fittedWidth * scale - width) / 2);
    const boundY = Math.max(0, (fittedHeight * scale - height) / 2);
    x = limit(x, -boundX, boundX);
    y = limit(y, -boundY, boundY);
    image.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
  }

  function select(nextIndex) {
    index = limit(nextIndex, 0, images.length - 1);
    scale = 1;
    x = 0;
    y = 0;
    gesture = null;
    image.alt = images[index].alt;
    image.src = photoUrl(images[index].src, 1600);
    counter.textContent = `${index + 1} / ${images.length}`;
    Array.from(thumbnails.children).forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
    draw();
  }

  images.forEach((item, i) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ochre-mobile-photo__thumb';
    button.setAttribute('aria-label', item.alt || `${i + 1} / ${images.length}`);
    const thumbnail = document.createElement('img');
    thumbnail.alt = '';
    thumbnail.src = photoUrl(item.src, 160);
    button.append(thumbnail);
    button.addEventListener('click', () => select(i));
    thumbnails.append(button);
  });

  function resize() {
    if (!mobilePhotoQuery.matches) return close();
    dialog.style.setProperty('--ochre-photo-height', `${window.visualViewport?.height || window.innerHeight}px`);
    draw();
  }

  function close() {
    if (dialog.open) dialog.close();
    dispose();
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    document.body.style.overflow = previousOverflow;
    window.removeEventListener('resize', resize);
    window.visualViewport?.removeEventListener('resize', resize);
    if (activeMobilePhoto?.dialog === dialog) activeMobilePhoto = null;
    dialog.remove();
  }
  dialog.addEventListener('close', dispose, { once: true });
  closeButton.addEventListener('click', close);
  image.addEventListener('load', draw);
  dialog.addEventListener('keydown', event => {
    if (event.key === 'ArrowLeft') { event.preventDefault(); select(index - 1); }
    if (event.key === 'ArrowRight') { event.preventDefault(); select(index + 1); }
  });
  stage.addEventListener('touchstart', event => {
    if (event.touches.length === 2) {
      event.preventDefault();
      gesture = { type: 'pinch', distance: distance(event.touches[0], event.touches[1]), scale, x, y, center: midpoint(event.touches[0], event.touches[1]) };
    } else if (event.touches.length === 1) {
      const touch = event.touches[0];
      gesture = { type: 'pan', startX: touch.clientX, startY: touch.clientY, x, y, dx: 0, dy: 0 };
    }
  }, { passive: false });
  stage.addEventListener('touchmove', event => {
    if (!gesture) return;
    event.preventDefault();
    if (gesture.type === 'pinch' && event.touches.length === 2) {
      const center = midpoint(event.touches[0], event.touches[1]);
      const rect = stage.getBoundingClientRect();
      const nextScale = limit(gesture.scale * distance(event.touches[0], event.touches[1]) / Math.max(1, gesture.distance), 1, 4);
      const ratio = nextScale / gesture.scale;
      x = center.x - rect.x - rect.width / 2 - (gesture.center.x - rect.x - rect.width / 2 - gesture.x) * ratio;
      y = center.y - rect.y - rect.height / 2 - (gesture.center.y - rect.y - rect.height / 2 - gesture.y) * ratio;
      scale = nextScale;
      draw();
    } else if (gesture.type === 'pan' && event.touches.length === 1) {
      gesture.dx = event.touches[0].clientX - gesture.startX;
      gesture.dy = event.touches[0].clientY - gesture.startY;
      if (scale > 1) { x = gesture.x + gesture.dx; y = gesture.y + gesture.dy; draw(); }
    }
  }, { passive: false });
  stage.addEventListener('touchend', event => {
    if (event.touches.length > 0) { gesture = null; return; }
    if (gesture?.type === 'pan' && scale === 1 && Math.abs(gesture.dx) > 50 && Math.abs(gesture.dx) > Math.abs(gesture.dy)) {
      select(index + (gesture.dx < 0 ? 1 : -1));
    }
    gesture = null;
  });
  stage.addEventListener('touchcancel', () => { gesture = null; });
  document.body.append(dialog);
  document.body.style.overflow = 'hidden';
  activeMobilePhoto = { dialog, close };
  select(index);
  resize();
  dialog.showModal();
  closeButton.focus({ preventScroll: true });
  window.addEventListener('resize', resize);
  window.visualViewport?.addEventListener('resize', resize);
  return activeMobilePhoto;
}

window.addEventListener('click', event => {
  if (!mobilePhotoQuery.matches || !(event.target instanceof Element)) return;
  const container = event.target.closest('.product-media-container--image');
  const gallery = container?.closest('media-gallery');
  if (!gallery || container.closest('dialog')) return;
  const sourceImages = Array.from(gallery.querySelectorAll('.dialog-zoomed-gallery > .product-media-container--image .product-media__image'));
  const images = sourceImages.map(item => ({ src: item.getAttribute('data_max_resolution') || item.currentSrc || item.src, alt: item.alt }));
  if (!images.length) return;
  const selectedId = container.querySelector('.product-media')?.getAttribute('data-media-id');
  const index = Math.max(0, sourceImages.findIndex(item => item.closest('.product-media')?.getAttribute('data-media-id') === selectedId));
  const config = document.querySelector('script[data-ochre-mobile-photo]');
  const label = document.querySelector('main h1')?.textContent.trim() || images[index].alt;
  event.preventDefault();
  event.stopImmediatePropagation();
  openMobilePhoto(images, index, label, config?.getAttribute('data-close-label') || '×');
}, { capture: true });
